/**
 * "Load alignments" for a LandXML layer the viewer has not loaded.
 *
 * `LandXML.tsx` is the only producer for `alignmentsAtom`, and the viewer
 * mounts it for SELECTED layers only. So a project whose alignments live in a
 * LandXML the user has not switched on has, as far as the station-volumes
 * section is concerned, no alignments at all — and the only way out was to
 * leave the analysis, switch the layer on, come back, and rebuild the whole
 * configuration.
 *
 * This hook lists those layers and reads one on request. Deliberately on
 * request, not automatically: a LandXML can be hundreds of megabytes, and it
 * is not worth downloading every one of them in case a centreline is in there.
 * `alignmentsFromLandxmlText` skips the TINs, so the parse itself is cheap —
 * the download is what is being rationed.
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import type * as THREE from 'three';

import { alignmentsAtom, registerAlignmentsAtom } from '@/atoms/alignment.atom';
import { buildAlignment, type Alignment } from '@/core/alignment';
import { layersAtom, layerSettingsManyAtom, type LayerData } from '@/atoms/layers.atom';
import { getResourcesEndpoint } from '@/core/resources';
import { alignmentsFromLandxmlText } from '@/lib/landxml/layerAlignments';
import { getLayerTransform } from '@/lib/layerTransform';
import { toOfflineUrl, useServeOffline } from '@/lib/offline/offlineResourceBase';

/** Per-layer outcome of the last attempt. `undefined` = not tried yet. */
export type AlignmentLoadState = 'loading' | 'empty' | 'failed';

export interface UnloadedAlignmentLayer {
  id: string;
  name: string;
  state?: AlignmentLoadState;
}

const isLandxmlLayer = (l: LayerData) =>
  l.type === 'MODEL' && (l.metadata as { type?: string } | null)?.type === 'LANDXML';

export function useUnloadedAlignmentLayers() {
  const allLayers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const registerAlignments = useSetAtom(registerAlignmentsAtom);
  // `useServeOffline` ignores its argument; the desktop build serves from disk.
  const serveOffline = useServeOffline();
  const [states, setStates] = useState<Record<string, AlignmentLoadState>>({});

  const landxmlLayers = useMemo(() => allLayers.filter(isLandxmlLayer), [allLayers]);
  // `layerSettingsManyAtom`, not `useLayerSettingsMany`: the latter opens a
  // react-query `useQueries`, and this section is shared by dialogs that are
  // mounted in several places. The atom resolves the per-user override and the
  // `layers` query's own `settings` selection, which covers every transform a
  // user has actually set. Anything it cannot see is corrected the moment the
  // layer is switched on and `LandXML.tsx` re-registers the same owner key.
  const getLayerSettings = useAtomValue(layerSettingsManyAtom);
  const settings = useMemo(
    () => getLayerSettings(landxmlLayers.map((l) => l.id)),
    [getLayerSettings, landxmlLayers],
  );

  /** Layer ids that already have at least one alignment in the registry. */
  const loadedOwners = useMemo(() => {
    const owners = new Set<string>();
    for (const a of alignments.values()) owners.add(a.layerId);
    return owners;
  }, [alignments]);

  const candidates = useMemo<UnloadedAlignmentLayer[]>(
    () =>
      landxmlLayers
        .filter((l) => !!l.root && !loadedOwners.has(l.id))
        // A rotated layer is skipped: `LandXML.tsx` rotates its alignments
        // about the load-time origin (the first vertex of the whole file,
        // surfaces included), and the light load never sees that vertex. An
        // offset alone needs no pivot, so those are fine. Switching the layer
        // on is still the way to use a rotated one.
        .filter((l) => !getLayerTransform(settings[l.id]).hasRotation)
        .map((l) => ({ id: l.id, name: l.name, state: states[l.id] }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [landxmlLayers, loadedOwners, settings, states],
  );

  const load = useCallback(
    async (layerId: string) => {
      const layer = landxmlLayers.find((l) => l.id === layerId);
      if (!layer?.root) return;
      setStates((s) => ({ ...s, [layerId]: 'loading' }));
      try {
        const httpsUrl = `${getResourcesEndpoint()}/${layer.root}`;
        const response = await fetch(serveOffline ? toOfflineUrl(httpsUrl) : httpsUrl, {
          ...(serveOffline ? {} : { credentials: 'include' as const }),
        });
        // A non-2xx still has a body — an S3/CloudFront XML error document,
        // which parses into a well-formed doc with no <Alignments> in it. Left
        // unchecked that reads to the user as "this file has no alignments".
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        const found = alignmentsFromLandxmlText(text, layerId);
        if (found.length === 0) {
          setStates((s) => ({ ...s, [layerId]: 'empty' }));
          return;
        }
        const { hasOffset, offsetVec } = getLayerTransform(settings[layerId]);
        registerAlignments({
          ownerKey: layerId,
          alignments: hasOffset ? found.map((a) => shiftAlignment(a, offsetVec)) : found,
        });
        // No entry at all once it worked: the layer leaves `candidates` by
        // virtue of being in the registry, and a stale "loading" would outlive
        // the row it belonged to.
        setStates((s) => {
          const next = { ...s };
          delete next[layerId];
          return next;
        });
      } catch (err) {
        console.error(`[landxml] could not read alignments for layer ${layerId}:`, err);
        setStates((s) => ({ ...s, [layerId]: 'failed' }));
      }
    },
    [landxmlLayers, registerAlignments, serveOffline, settings],
  );

  return { candidates, load };
}

/** `world = p + offset` — the offset-only half of `LandXML.tsx`'s `toWorld`,
 *  rebuilt through `buildAlignment` (and stripping the synthetic START/END it
 *  re-adds) for the same reason every other caller does. */
function shiftAlignment(a: Alignment, offset: THREE.Vector3): Alignment {
  const points = new Float64Array(a.points);
  for (let i = 0; i < points.length; i += 3) {
    points[i] += offset.x;
    points[i + 1] += offset.y;
    points[i + 2] += offset.z;
  }
  return (
    buildAlignment({
      ...a,
      points,
      keyPoints: a.keyPoints.filter((k) => k.kind !== 'START' && k.kind !== 'END'),
    }) ?? a
  );
}
